import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {AccelDeathBenefitType, GPLU_ACDB_FIELD_ID, GPLUCoverageCode} from '../../../PlanTemplates/GWL/gpluConstants';
import {showInflationProtectionOrOptions} from '../../../PlanTemplates/GWL/GWLUtilities';
import {getGPLUAccelBenefitScheduleBlockPropertyPaths, getGPLUCoverageDefinitionPropertyPaths} from '../PropertyPaths';

export const GPLUAccelDeathBenefitScheduleBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();

  const {selectedPlan, selectedCoverageIndex} = useSelector((state: ReducerState) => state.quoteForm);
  const situsStateCd = getQuoteFieldValue<string>('riskStateCd');
  const accelDeathBenefitType = getQuoteFieldValue<string>(
    getGPLUCoverageDefinitionPropertyPaths(selectedPlan?.planIndex, selectedCoverageIndex).accelDeathBenefitTypeCd
  );

  const propertyPaths = getGPLUAccelBenefitScheduleBlockPropertyPaths(selectedPlan?.planIndex, selectedCoverageIndex);
  const accelBenefitType = getQuoteFieldValue<string>(
    getGPLUCoverageDefinitionPropertyPaths(selectedPlan?.planIndex, selectedCoverageIndex).accelDeathBenefitTypeCd
  );
  const pctDeathBenefit = getQuoteFieldValue<number>(propertyPaths.monthlyDeathBenefitPct);

  const getMaxBenefitTimePeriod = useMemo(() => {
    if (accelBenefitType === AccelDeathBenefitType.LTC_Basic) {
      return pctDeathBenefit === 0.04 ? 25 : 17;
    }
    if (accelBenefitType === AccelDeathBenefitType.LTC_Restoration_Extension) {
      return pctDeathBenefit === 0.04 ? 50 : 34;
    }
  }, [accelBenefitType, pctDeathBenefit]);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'row-5e8a5fed-7788-4188-b68e-c684462de63f'},
      items: [
        {
          key: propertyPaths.benefitTypeLTCChronicCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Benefit Type"
              id={propertyPaths.benefitTypeLTCChronicCd}
              coverageCode={GPLUCoverageCode.ACCEL_DEATH_BENEFIT_LIFE}
              searchKey="GPLUBenefitScheduleLTCChronic.benefitTypeLTCChronicCd"
              value={getQuoteFieldValue(propertyPaths.benefitTypeLTCChronicCd)}
              onChange={e => updateQuoteFieldValue(propertyPaths.benefitTypeLTCChronicCd, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.inflationProtection,
          hidden: !showInflationProtectionOrOptions(accelDeathBenefitType as AccelDeathBenefitType, situsStateCd),
          element: (
            <QuoteFormElement
              elementType="FormControl"
              label="Inflation Protection"
              id={propertyPaths.inflationProtection}
              checked={getQuoteFieldValue<boolean>(propertyPaths.inflationProtection)}
              onChange={e => updateQuoteFieldValue(propertyPaths.inflationProtection, e.target.checked)}
              controlType="checkbox"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.monthlyDeathBenefitPct,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Percentage of Death Benefit Amount (Monthly)"
              id={propertyPaths.monthlyDeathBenefitPct}
              coverageCode={GPLUCoverageCode.ACCEL_DEATH_BENEFIT_LIFE}
              searchKey="GPLUBenefitScheduleLTCChronic.monthlyDeathBenefitPct"
              value={getQuoteFieldValue(propertyPaths.monthlyDeathBenefitPct)}
              formFieldId={GPLU_ACDB_FIELD_ID.monthlyDeathBenefitPct}
              onChange={e =>
                updateQuoteFieldValues([
                  {path: propertyPaths.monthlyDeathBenefitPct, newValue: Number(e.target.value)},
                  {
                    path: propertyPaths.maxBenefitTimePeriod,
                    newValue: getMaxBenefitTimePeriod,
                  },
                ])
              }
              disabled={situsStateCd === 'MD'}
              type="number"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.maxBenefitTimePeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Time Period (Months)"
              id={propertyPaths.maxBenefitTimePeriod}
              value={getMaxBenefitTimePeriod}
              onChange={e => updateQuoteFieldValue(propertyPaths.maxBenefitTimePeriod, Number(e.target.value))}
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              type="number"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Benefit Schedule" collapsible withSeparator expanded>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
